import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Code as CodeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { downloadJson } from '../../utils/fileUtils';

/**
 * AttributeEditor - Component for editing individual attribute values
 * 
 * @param {object} props
 * @param {string} props.attribute - The attribute name
 * @param {any} props.value - The attribute value
 * @param {string} props.codexId - The codex ID
 * @param {boolean} props.isEditing - Whether in editing mode
 * @param {function} props.onChange - Function called when value changes
 * @param {function} props.onJsonError - Function called when JSON errors occur
 */
const AttributeEditor = ({ attribute, value, codexId, isEditing, onChange, onJsonError }) => {
  // Initialize with properly formatted value
  const [editValue, setEditValue] = useState(
    typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')
  );
  const [hasError, setHasError] = useState(false);

  // Reset value when external value changes OR when edit mode changes
  useEffect(() => {
    const formattedValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '');
    
    // Always reset when not editing, or when the value prop actually changes
    if (!isEditing || editValue !== formattedValue) {
      setEditValue(formattedValue);
      setHasError(false);
      // Clear any error when resetting
      if (hasError && onJsonError) {
        onJsonError(attribute, false);
      }
    }
  }, [value, isEditing, attribute, onJsonError, hasError, editValue]);

  // Is this a JSON-like value?
  const isJsonValue = typeof value === 'object' || 
    (typeof value === 'string' && (value.trim().startsWith('{') || value.trim().startsWith('[')));

  // Handle value changes
  const handleChange = (newValue) => {
    setEditValue(newValue);
    
    // Check for JSON format if it looks like JSON
    if (newValue.trim().startsWith('{') || newValue.trim().startsWith('[')) {
      try {
        // Try parsing as JSON
        const parsedValue = JSON.parse(newValue);
        onChange(attribute, parsedValue);
        
        // Clear any existing error
        if (hasError) {
          setHasError(false);
          onJsonError?.(attribute, false);
        }
      } catch (error) {
        // Mark as error if it's not already marked
        if (!hasError) {
          setHasError(true);
          onJsonError?.(attribute, true);
        }
        onChange(attribute, newValue);
      }
    } else {
      // Handle as regular text
      onChange(attribute, newValue);
      if (hasError) {
        setHasError(false);
        onJsonError?.(attribute, false);
      }
    }
  };

  // Download JSON content
  const handleDownloadJson = () => {
    const jsonData = typeof value === 'object' ? value : { [attribute]: value };
    downloadJson(jsonData, `${codexId}_${attribute}`);
  };

  // Render editing mode
  if (isEditing) {
    return (
      <TextField
        fullWidth
        size="small"
        multiline={isJsonValue}
        rows={isJsonValue ? 4 : 1}
        value={editValue}
        onChange={(e) => handleChange(e.target.value)}
        error={hasError}
        helperText={hasError ? "Invalid JSON syntax" : null}
      />
    );
  }

  // Render JSON value display
  if (isJsonValue) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Chip 
            icon={<CodeIcon />}
            label="JSON"
            size="small"
            color="primary"
            variant="outlined"
          />
          <Tooltip title="Download JSON">
            <IconButton size="small" onClick={handleDownloadJson}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box 
          component="pre"
          sx={{ 
            p: 1,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '200px',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#eeeeee'
            }
          }}
        >
          {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
        </Box>
      </Box>
    );
  }

  // Render regular value
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : (value ?? '-')}
    </Typography>
  );
};

export default AttributeEditor; 